import React, { useState } from 'react';
import { X, Mail, User, Building2, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OfferModal = ({ isOpen, onClose, configuration }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientCompany: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/generate-offer`, {
        ...formData,
        configuration
      });

      if (response.data.success) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setFormData({ clientName: '', clientEmail: '', clientCompany: '' });
          setSubmitStatus(null);
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error generating offer:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <Card className="relative w-full max-w-md bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          data-testid="offer-close-btn"
          className="absolute top-4 right-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t("offerModal.title")}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t("offerModal.subtitle")}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="offer-form">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="clientName" className="text-gray-900 dark:text-white flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              {t("offerModal.yourName")} *
            </Label>
            <Input
              id="clientName"
              name="clientName"
              data-testid="offer-clientName-input"
              value={formData.clientName}
              onChange={handleInputChange}
              placeholder={t("offerModal.namePlaceholder")}
              className="bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white placeholder:text-gray-500 focus:border-blue-500"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="clientEmail" className="text-gray-900 dark:text-white flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              {t("offerModal.yourEmail")} *
            </Label>
            <Input
              id="clientEmail"
              name="clientEmail"
              data-testid="offer-clientEmail-input"
              type="email"
              value={formData.clientEmail}
              onChange={handleInputChange}
              placeholder={t("offerModal.emailPlaceholder")}
              className="bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white placeholder:text-gray-500 focus:border-blue-500"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Company Field */}
          <div className="space-y-2">
            <Label htmlFor="clientCompany" className="text-gray-900 dark:text-white flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              {t("offerModal.companyName")}
            </Label>
            <Input
              id="clientCompany"
              name="clientCompany"
              data-testid="offer-clientCompany-input"
              value={formData.clientCompany}
              onChange={handleInputChange}
              placeholder={t("offerModal.companyPlaceholder")}
              className="bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white placeholder:text-gray-500 focus:border-blue-500"
              disabled={isSubmitting}
            />
          </div>

          {/* Configuration Summary */}
          <div className="p-4 bg-gray-100 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t("offerModal.yourConfig")}</p>
            <div className="space-y-1">
              {configuration.service && (
                <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">{configuration.service}</p>
              )}
              <p className="text-gray-900 dark:text-white font-semibold">
                Setup: CHF {configuration.setupPrice.toLocaleString('de-CH').replace(/,/g, "'")}
              </p>
              <p className="text-blue-600 dark:text-blue-400 font-semibold">
                Monthly: CHF {configuration.monthlyPrice.toLocaleString('de-CH').replace(/,/g, "'")}.– / month
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {configuration.basePackage.length + configuration.selectedAddons.length} {t("offerModal.features")}
                {configuration.employees ? ` • ${configuration.employees} ${t("offerModal.employees")}` : ''}
              </p>
            </div>
          </div>

          {/* Success/Error Messages */}
          {submitStatus === 'success' && (
            <div className="p-3 bg-green-600/20 border border-green-500/30 rounded-lg text-green-600 dark:text-green-400 text-sm" data-testid="offer-success-message">
              {t("offerModal.successMessage")}
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="p-3 bg-red-600/20 border border-red-500/30 rounded-lg text-red-600 dark:text-red-400 text-sm" data-testid="offer-error-message">
              {t("offerModal.errorMessage")}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            data-testid="offer-submit-btn"
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-6 shadow-xl"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {t("offerModal.generating")}
              </>
            ) : (
              <>
                <Mail className="w-5 h-5 mr-2" />
                {t("offerModal.sendOffer")}
              </>
            )}
          </Button>

          <p className="text-xs text-center text-gray-500">
            You'll receive a detailed PDF quote via email within minutes
          </p>
        </form>
      </Card>
    </div>
  );
};

export default OfferModal;
